import { useEffect, useMemo, useState } from 'react';
import { Check, ShoppingCart, X } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  createCheckoutSession,
  getAssignedPromotion,
  getProductCatalog,
} from '../../services/api';
import { useAuthSession } from '../../contexts/AuthSessionContext';
import { ensureActiveSession } from '../../services/session';
import { hasActivePromotionAccessTrial } from '../../utils/billing';
import { getApiErrorMessage } from '../../utils/apiError';

export const PRODUCT_CHECKOUT_CATALOG = [
  {
    key: 'atendabot',
    accessKey: 'atendabot',
    name: 'AtendaBot',
    description: 'Atendimento com IA no WhatsApp. One Menu incluso.',
    priceCents: 39000,
  },
  {
    key: 'reservo',
    accessKey: 'reservo',
    name: 'Reservo',
    description: 'Reservas online e confirmação pelo WhatsApp.',
    priceCents: 14900,
  },
  {
    key: 'one_menu',
    accessKey: 'cardapio',
    name: 'One Menu',
    description: 'Cardápio digital com link público e QR Code.',
    priceCents: 12900,
  },
  {
    key: 'feedback_hero',
    accessKey: 'feedback_hero',
    name: 'Feedback Hero',
    description: 'Avaliações por estrelas e comentários centralizados.',
    priceCents: 8900,
  },
  {
    key: 'curriculo_certo',
    accessKey: 'cv',
    name: 'Currículo Certo',
    description: 'Recrutamento para operações de food service.',
    priceCents: 4900,
  },
];

const PRODUCT_KEY_ALIASES = {
  atendabot: 'atendabot',
  atenda_bot: 'atendabot',
  reservo: 'reservo',
  one_menu: 'one_menu',
  onemenu: 'one_menu',
  cardapio: 'one_menu',
  feedback_hero: 'feedback_hero',
  feedbackhero: 'feedback_hero',
  feedback: 'feedback_hero',
  curriculo_certo: 'curriculo_certo',
  curriculocerto: 'curriculo_certo',
  curriculo: 'curriculo_certo',
  cv: 'curriculo_certo',
};

const DISCOUNT_BY_COUNT = {
  1: 0,
  2: 5,
  3: 10,
  4: 15,
  5: 20,
};

const INCLUDED_PRODUCT_KEYS_BY_PRODUCT = {
  atendabot: ['one_menu'],
};

const EMPTY_PRODUCT_KEYS = [];
const EMPTY_PRODUCT_DESCRIPTION_OVERRIDES = {};

const emailRegex = /^\S+@\S+\.\S+$/;

export const normalizeCheckoutProductKey = (value) => {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim().toLowerCase().replace(/[\s-]+/g, '_');
  return PRODUCT_KEY_ALIASES[normalized] || null;
};

const formatCurrency = (amountCents) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(Number(amountCents || 0) / 100);

const getCatalogProduct = (productKey, catalog = PRODUCT_CHECKOUT_CATALOG) =>
  catalog.find((product) => product.key === productKey) || null;

const uniqueProductKeys = (productKeys = []) => {
  const seen = new Set();
  const result = [];
  productKeys.forEach((productKey) => {
    if (!productKey || seen.has(productKey) || !getCatalogProduct(productKey)) return;
    seen.add(productKey);
    result.push(productKey);
  });
  return result;
};

const expandIncludedProductKeys = (productKeys = []) => {
  const expandedKeys = [];
  productKeys.forEach((productKey) => {
    expandedKeys.push(productKey);
    expandedKeys.push(...(INCLUDED_PRODUCT_KEYS_BY_PRODUCT[productKey] || []));
  });
  return uniqueProductKeys(expandedKeys);
};

const getFreeIncludedProductKeys = (selectedKeys = [], activeKeys = []) => {
  const activeSet = new Set(expandIncludedProductKeys(activeKeys));
  const includedKeys = [];

  selectedKeys.forEach((productKey) => {
    (INCLUDED_PRODUCT_KEYS_BY_PRODUCT[productKey] || []).forEach((includedKey) => {
      if (!activeSet.has(includedKey)) {
        includedKeys.push(includedKey);
      }
    });
  });

  return uniqueProductKeys(includedKeys);
};

const getBillableProductKeys = (selectedKeys) => {
  if (!selectedKeys.includes('atendabot')) return selectedKeys;
  return selectedKeys.filter((key) => key !== 'one_menu');
};

const calculatePricedProducts = (
  productKeys = [],
  discountPercent = 0,
  catalog = PRODUCT_CHECKOUT_CATALOG
) =>
  productKeys
    .map((key) => getCatalogProduct(key, catalog))
    .filter(Boolean)
    .map((product) => {
      const discountCents = Math.round((product.priceCents * discountPercent) / 100);
      return {
        ...product,
        discountCents,
        finalPriceCents: product.priceCents - discountCents,
      };
    });

const buildPriceByKey = (products = []) =>
  products.reduce((acc, product) => {
    acc[product.key] = product;
    return acc;
  }, {});

const getProgressiveDiscountPercent = (productCount) =>
  DISCOUNT_BY_COUNT[Math.min(Math.max(Number(productCount) || 1, 1), 5)] || 0;

const getIncludedProductKeysForProductKeys = (productKeys = []) => {
  const billableSet = new Set(getBillableProductKeys(uniqueProductKeys(productKeys)));
  return expandIncludedProductKeys(productKeys).filter((key) => !billableSet.has(key));
};

export const calculateProductCheckoutSummary = (
  selectedKeys = [],
  activeKeys = [],
  options = {},
) => {
  const catalog = Array.isArray(options.catalog) && options.catalog.length
    ? options.catalog
    : PRODUCT_CHECKOUT_CATALOG;
  const activePricingByKey = (Array.isArray(options.activePricingProducts)
    ? options.activePricingProducts
    : []).reduce((acc, product) => {
    const key = normalizeCheckoutProductKey(product?.key);
    if (key) acc[key] = product;
    return acc;
  }, {});
  const assignedPromotion = options.promotion || null;
  const promotionProductsByKey = (Array.isArray(assignedPromotion?.products)
    ? assignedPromotion.products
    : []).reduce((acc, product) => {
    const key = normalizeCheckoutProductKey(product?.product_key);
    if (key) acc[key] = product;
    return acc;
  }, {});
  const selectedProductKeys = uniqueProductKeys(selectedKeys);
  const activeProductKeys = expandIncludedProductKeys(activeKeys);
  const activeProductKeySet = new Set(activeProductKeys);
  const activeBillableProductKeys = getBillableProductKeys(uniqueProductKeys(
    Array.isArray(options.activeBillableKeys) && options.activeBillableKeys.length
      ? options.activeBillableKeys
      : activeKeys
  ));
  const billableKeys = getBillableProductKeys(selectedProductKeys)
    .filter((key) => !activeProductKeySet.has(key));
  const futureBillableProductKeys = getBillableProductKeys(uniqueProductKeys([
    ...activeBillableProductKeys,
    ...billableKeys,
  ]));
  const includedKeys = getFreeIncludedProductKeys(selectedProductKeys, activeProductKeys);
  const futureIncludedKeys = getIncludedProductKeysForProductKeys(futureBillableProductKeys);
  const currentDiscountKeys = activeBillableProductKeys;
  const futureDiscountKeys = futureBillableProductKeys;
  const currentDiscountPercent = currentDiscountKeys.length
    ? getProgressiveDiscountPercent(currentDiscountKeys.length)
    : 0;
  const discountProductCount = futureDiscountKeys.length;
  const discountPercent = discountProductCount
    ? getProgressiveDiscountPercent(discountProductCount)
    : 0;

  const applyContractedActivePrice = (product, { preserveOnlyPromotion = false } = {}) => {
    const contracted = activePricingByKey[product.key];
    if (!contracted) return product;
    if (preserveOnlyPromotion && contracted.priceOrigin !== 'promotion') return product;
    const finalPriceCents = Number(contracted.discountedUnitAmount);
    if (!Number.isInteger(finalPriceCents) || finalPriceCents < 0) return product;
    return {
      ...product,
      finalPriceCents,
      discountCents: product.priceCents - finalPriceCents,
      priceOrigin: contracted.priceOrigin || product.priceOrigin,
      promotionId: contracted.promotionId || null,
    };
  };
  const applyAssignedPromotionPrice = (product) => {
    const promotionProduct = promotionProductsByKey[product.key];
    if (!promotionProduct) return product;
    const fixedAmount = Number(promotionProduct.promotional_amount_cents);
    const percentOff = Number(assignedPromotion?.percent_off);
    const finalPriceCents = assignedPromotion?.pricing_mode === 'fixed_price'
      ? fixedAmount
      : Math.max(0, product.priceCents - Math.round((product.priceCents * percentOff) / 100));
    if (!Number.isInteger(finalPriceCents) || finalPriceCents < 0) return product;
    return {
      ...product,
      finalPriceCents,
      discountCents: product.priceCents - finalPriceCents,
      priceOrigin: 'promotion',
      promotionId: assignedPromotion.id,
    };
  };
  const currentProducts = calculatePricedProducts(
    activeBillableProductKeys,
    currentDiscountPercent,
    catalog
  )
    .map((product) => applyContractedActivePrice(product));
  const futureProducts = calculatePricedProducts(
    futureBillableProductKeys,
    discountPercent,
    catalog
  )
    .map((product) => applyContractedActivePrice(product, { preserveOnlyPromotion: true }))
    .map(applyAssignedPromotionPrice);
  const products = futureProducts.filter((product) => billableKeys.includes(product.key));
  const futureActiveProducts = futureProducts.filter((product) =>
    activeBillableProductKeys.includes(product.key)
  );

  const subtotalCents = products.reduce((sum, product) => sum + product.priceCents, 0);
  const newProductsTotalCents = products.reduce((sum, product) => sum + product.finalPriceCents, 0);
  const currentSubtotalCents = currentProducts.reduce((sum, product) => sum + product.priceCents, 0);
  const currentTotalCents = currentProducts.reduce((sum, product) => sum + product.finalPriceCents, 0);
  const futureSubtotalCents = futureProducts.reduce((sum, product) => sum + product.priceCents, 0);
  const futureTotalCents = futureProducts.reduce((sum, product) => sum + product.finalPriceCents, 0);
  const promotionDiscountCents = futureProducts
    .filter((product) => product.priceOrigin === 'promotion')
    .reduce((sum, product) => sum + product.discountCents, 0);
  const progressiveDiscountCents = futureProducts
    .filter((product) => product.priceOrigin !== 'promotion')
    .reduce((sum, product) => sum + product.discountCents, 0);
  const futureActiveTotalCents = futureActiveProducts.reduce(
    (sum, product) => sum + product.finalPriceCents,
    0
  );
  const activeAdjustmentCents = futureActiveTotalCents - currentTotalCents;
  const amountAddedCents = futureTotalCents - currentTotalCents;

  const includedProducts = includedKeys
    .map((key) => getCatalogProduct(key, catalog))
    .filter(Boolean);
  const futureIncludedProducts = futureIncludedKeys
    .map((key) => getCatalogProduct(key, catalog))
    .filter(Boolean);
  const includedCents = includedProducts.reduce((sum, product) => sum + product.priceCents, 0);
  const futureIncludedCents = futureIncludedProducts.reduce(
    (sum, product) => sum + product.priceCents,
    0
  );

  return {
    billableCount: billableKeys.length,
    activeCount: currentDiscountKeys.length,
    activeBillableProductKeys,
    activeProductKeys,
    discountProductCount,
    currentDiscountPercent,
    discountPercent,
    subtotalCents,
    currentSubtotalCents,
    currentTotalCents,
    futureSubtotalCents,
    futureTotalCents,
    futureActiveTotalCents,
    futureCatalogSubtotalCents: futureSubtotalCents + futureIncludedCents,
    catalogSubtotalCents: subtotalCents + includedCents,
    includedCents,
    futureIncludedCents,
    discountCents: futureSubtotalCents - futureTotalCents,
    promotionDiscountCents,
    progressiveDiscountCents,
    newProductsDiscountCents: subtotalCents - newProductsTotalCents,
    newProductsTotalCents,
    activeAdjustmentCents,
    amountAddedCents,
    totalCents: amountAddedCents,
    discountKeys: futureDiscountKeys,
    futureBillableProductKeys,
    futureDiscountKeys,
    includedProducts,
    futureIncludedProducts,
    currentProducts,
    futureProducts,
    currentProductPriceByKey: buildPriceByKey(currentProducts),
    futureProductPriceByKey: buildPriceByKey(futureProducts),
    products,
  };
};

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 1100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    background: 'rgba(15, 23, 42, 0.58)',
    backdropFilter: 'blur(8px)',
  },
  dialog: {
    width: 'min(720px, 100%)',
    maxHeight: 'min(760px, 92vh)',
    overflow: 'hidden',
    borderRadius: 18,
    background: '#ffffff',
    boxShadow: '0 24px 80px rgba(15, 23, 42, 0.28)',
    border: '1px solid rgba(15, 23, 42, 0.10)',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    padding: '18px 20px',
    borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
  },
  body: {
    padding: 20,
    overflowY: 'auto',
  },
  footer: {
    padding: 20,
    borderTop: '1px solid rgba(15, 23, 42, 0.08)',
    background: '#f8fafc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  iconButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    border: '1px solid rgba(15, 23, 42, 0.10)',
    background: '#ffffff',
    color: '#475569',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  primaryButton: {
    border: 0,
    borderRadius: 12,
    background: '#0891b2',
    color: '#ffffff',
    fontWeight: 800,
    padding: '12px 18px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
  secondaryButton: {
    border: '1px solid rgba(15, 23, 42, 0.12)',
    borderRadius: 12,
    background: '#ffffff',
    color: '#334155',
    fontWeight: 700,
    padding: '11px 16px',
    cursor: 'pointer',
  },
};

/**
 * @param {{
 *   open: boolean,
 *   onClose?: () => void,
 *   onActivated?: (data: any) => void,
 *   initialProductKey?: string | null,
 *   activeSubscriptionProductKeys?: string[],
 *   title?: string,
 *   allowedProductKeys?: string[] | null,
 *   checkoutBrandName?: string,
 *   checkoutDescription?: string,
 *   checkoutSummaryDescription?: string,
 *   productDescriptionOverrides?: Record<string, string>,
 * }} props
 */
const ProductCheckoutModal = ({
  open,
  onClose,
  onActivated,
  initialProductKey = null,
  activeSubscriptionProductKeys = EMPTY_PRODUCT_KEYS,
  activeSubscriptionPricing = EMPTY_PRODUCT_KEYS,
  title = 'Ativar produtos',
  allowedProductKeys = null,
  checkoutBrandName = 'Food Station',
  checkoutDescription = 'Selecione um ou mais produtos. O desconto progressivo é aplicado automaticamente.',
  checkoutSummaryDescription = 'AtendaBot e One Menu formam um único combo de R$ 390 e contam como um produto na faixa de desconto.',
  productDescriptionOverrides = EMPTY_PRODUCT_DESCRIPTION_OVERRIDES,
}) => {
  const {
    user,
    products: sessionProducts = {},
    isOwner,
    hasProductAccess,
    billing,
  } = useAuthSession();
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [assignedPromotion, setAssignedPromotion] = useState(null);
  const [promotionLoading, setPromotionLoading] = useState(false);
  const [promotionLookupFailed, setPromotionLookupFailed] = useState(false);
  const [productCatalog, setProductCatalog] = useState(PRODUCT_CHECKOUT_CATALOG);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogLookupFailed, setCatalogLookupFailed] = useState(false);
  const allowedProductKeysSignature = Array.isArray(allowedProductKeys)
    ? uniqueProductKeys(
      allowedProductKeys
        .map((productKey) => normalizeCheckoutProductKey(productKey))
        .filter(Boolean)
    ).sort().join('|')
    : null;
  const allowedProductKeySet = useMemo(
    () => (
      allowedProductKeysSignature === null
        ? null
        : new Set(allowedProductKeysSignature ? allowedProductKeysSignature.split('|') : [])
    ),
    [allowedProductKeysSignature]
  );
  const baseCheckoutCatalog = useMemo(
    () => PRODUCT_CHECKOUT_CATALOG.filter(
      (product) => allowedProductKeySet === null || allowedProductKeySet.has(product.key)
    ),
    [allowedProductKeySet]
  );
  const checkoutProductCatalog = useMemo(
    () => productCatalog
      .filter((product) => allowedProductKeySet === null || allowedProductKeySet.has(product.key))
      .map((product) => {
        const descriptionOverride = productDescriptionOverrides?.[product.key];
        return typeof descriptionOverride === 'string'
          ? { ...product, description: descriptionOverride }
          : product;
      }),
    [allowedProductKeySet, productCatalog, productDescriptionOverrides]
  );
  const scopedAssignedPromotion = useMemo(() => {
    if (!assignedPromotion || allowedProductKeySet === null) return assignedPromotion;

    const products = (assignedPromotion.products || []).filter((product) => {
      const productKey = normalizeCheckoutProductKey(product.product_key);
      return productKey && allowedProductKeySet.has(productKey);
    });

    return products.length ? { ...assignedPromotion, products } : null;
  }, [allowedProductKeySet, assignedPromotion]);
  const isTrialingBilling =
    String(billing?.status || '').trim().toLowerCase() === 'trialing';
  const isPromotionAccessTrial = hasActivePromotionAccessTrial({ billing });

  const activeKeys = useMemo(() => {
    const activeProductKeys = isTrialingBilling
      ? []
      : baseCheckoutCatalog
        .filter((product) => (
          isPromotionAccessTrial
            ? sessionProducts?.[product.accessKey] === true
            : typeof hasProductAccess === 'function'
            ? hasProductAccess(product.accessKey)
            : sessionProducts?.[product.accessKey] === true
        ))
        .map((product) => product.key);
    const subscriptionProductKeys = isTrialingBilling
      ? []
      : activeSubscriptionProductKeys
        .map((productKey) => normalizeCheckoutProductKey(productKey))
        .filter((productKey) => (
          productKey && (
            allowedProductKeySet === null || allowedProductKeySet.has(productKey)
          )
        ));
    return new Set(expandIncludedProductKeys(uniqueProductKeys([
      ...activeProductKeys,
      ...subscriptionProductKeys,
    ])));
  }, [
    activeSubscriptionProductKeys,
    allowedProductKeySet,
    baseCheckoutCatalog,
    hasProductAccess,
    isPromotionAccessTrial,
    isTrialingBilling,
    sessionProducts,
  ]);

  const activeBillableKeys = useMemo(() => {
    const explicitKeys = uniqueProductKeys(
      activeSubscriptionProductKeys.map((productKey) => normalizeCheckoutProductKey(productKey))
    ).filter((productKey) => (
      allowedProductKeySet === null || allowedProductKeySet.has(productKey)
    ));
    if (explicitKeys.length) {
      return getBillableProductKeys(explicitKeys);
    }
    return getBillableProductKeys(Array.from(activeKeys));
  }, [activeKeys, activeSubscriptionProductKeys, allowedProductKeySet]);
  const checkoutLockedForExistingSubscription = activeBillableKeys.length > 0 && !isOwner;

  useEffect(() => {
    if (!open) return;
    const normalizedInitialKey = normalizeCheckoutProductKey(initialProductKey);
    const checkoutInitialKey = normalizedInitialKey === 'one_menu'
      ? 'atendabot'
      : normalizedInitialKey;
    const nextSelected = checkoutInitialKey
      && (allowedProductKeySet === null || allowedProductKeySet.has(checkoutInitialKey))
      && !activeKeys.has(checkoutInitialKey)
      ? [checkoutInitialKey]
      : [];
    setSelectedKeys(nextSelected);
    setErrorMessage('');
    setSubmitting(false);
  }, [activeKeys, allowedProductKeySet, initialProductKey, open]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setAssignedPromotion(null);
    setPromotionLookupFailed(false);
    setPromotionLoading(true);
    getAssignedPromotion()
      .then((response) => {
        if (!active) return;
        const promotion = response?.data || null;
        setAssignedPromotion(promotion);
        if (promotion) {
          const promotionKeys = (promotion.products || [])
            .map((product) => normalizeCheckoutProductKey(product.product_key))
            .filter((productKey) => (
              productKey
              && (allowedProductKeySet === null || allowedProductKeySet.has(productKey))
              && !activeKeys.has(productKey)
            ));
          setSelectedKeys((current) => uniqueProductKeys([...promotionKeys, ...current]));
        }
      })
      .catch(() => {
        if (!active) return;
        setPromotionLookupFailed(true);
        setErrorMessage('Não foi possível verificar sua oferta promocional. Tente novamente.');
      })
      .finally(() => { if (active) setPromotionLoading(false); });
    return () => { active = false; };
  }, [activeKeys, allowedProductKeySet, open]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setCatalogLookupFailed(false);
    setCatalogLoading(true);
    getProductCatalog()
      .then((response) => {
        if (!active) return;
        const remoteCatalog = Array.isArray(response?.data) ? response.data : [];
        const pricesByKey = Object.fromEntries(
          remoteCatalog.map((product) => [product.key, Number(product.priceCents)])
        );
        const hasEveryPrice = baseCheckoutCatalog.every(
          (product) => Number.isInteger(pricesByKey[product.key])
            && pricesByKey[product.key] >= 0
        );
        if (!hasEveryPrice) {
          throw new Error('Catálogo de produtos incompleto');
        }
        setProductCatalog(PRODUCT_CHECKOUT_CATALOG.map((product) => ({
          ...product,
          priceCents: Number.isInteger(pricesByKey[product.key])
            ? pricesByKey[product.key]
            : product.priceCents,
        })));
      })
      .catch(() => {
        if (!active) return;
        setCatalogLookupFailed(true);
      })
      .finally(() => { if (active) setCatalogLoading(false); });
    return () => { active = false; };
  }, [baseCheckoutCatalog, open]);

  if (!open) return null;

  const summary = calculateProductCheckoutSummary(
    selectedKeys,
    Array.from(activeKeys),
    {
      activeBillableKeys,
      activePricingProducts: activeSubscriptionPricing,
      catalog: checkoutProductCatalog,
      promotion: scopedAssignedPromotion,
    }
  );
  const selectedSet = new Set(selectedKeys);
  const assignedPromotionProductKeys = new Set(
    (scopedAssignedPromotion?.products || [])
      .map((product) => normalizeCheckoutProductKey(product.product_key))
      .filter(Boolean)
  );
  const fixedPromotionAmountsByKey = scopedAssignedPromotion?.pricing_mode === 'fixed_price'
    ? Object.fromEntries(
      (scopedAssignedPromotion.products || []).map((product) => [
        normalizeCheckoutProductKey(product.product_key),
        Number(product.promotional_amount_cents),
      ])
    )
    : {};
  const selectedBillableKeys = getBillableProductKeys(uniqueProductKeys(selectedKeys));
  const canCheckoutWithFixedPromotionWithoutCatalog =
    selectedBillableKeys.length > 0
    && selectedBillableKeys.every((productKey) => (
      Number.isInteger(fixedPromotionAmountsByKey[productKey])
      && fixedPromotionAmountsByKey[productKey] >= 0
    ));
  const catalogBlocksCheckout =
    catalogLookupFailed && !canCheckoutWithFixedPromotionWithoutCatalog;
  const remainingAvailableCount = checkoutProductCatalog.filter(
    (product) =>
      !activeKeys.has(product.key) &&
      !selectedSet.has(product.key) &&
      !(product.key === 'one_menu' && selectedSet.has('atendabot'))
  ).length;

  const toggleProduct = (productKey) => {
    const checkoutProductKey = productKey === 'one_menu' ? 'atendabot' : productKey;
    if (checkoutLockedForExistingSubscription) return;
    if (activeKeys.has(checkoutProductKey)) return;
    if (assignedPromotionProductKeys.has(checkoutProductKey)) return;

    setErrorMessage('');
    setSelectedKeys((current) => {
      if (current.includes(checkoutProductKey)) {
        return current.filter((key) => key !== checkoutProductKey);
      }

      const withNext = [...current, checkoutProductKey];
      if (checkoutProductKey === 'atendabot') {
        return withNext.filter((key) => key !== 'one_menu');
      }
      return withNext;
    });
  };

  const handleSubmit = async () => {
    if (checkoutLockedForExistingSubscription) {
      toast.error('Somente o proprietário pode alterar produtos de uma assinatura ativa.');
      return;
    }
    if (summary.billableCount === 0 || submitting) return;
    if (promotionLoading || promotionLookupFailed || catalogLoading || catalogBlocksCheckout) {
      toast.error('Aguarde a validação da oferta e dos preços atuais.');
      return;
    }
    setSubmitting(true);
    setErrorMessage('');

    try {
      const payloadProductKeys = selectedKeys.filter((productKey) => (
        allowedProductKeySet === null || allowedProductKeySet.has(productKey)
      ));
      const payload = { products: payloadProductKeys };
      const email = String(user?.username || '').trim();
      if (emailRegex.test(email)) {
        payload.email = email;
      }

      const response = await createCheckoutSession(payload);
      const responseData = response?.data || {};
      if (responseData.subscriptionUpdated) {
        toast.success('Produto adicionado à assinatura.');
        await ensureActiveSession({ force: true }).catch(() => null);
        onActivated?.(responseData);
        onClose?.();
        setSubmitting(false);
        return;
      }

      const url = responseData.url;
      if (!url) {
        throw new Error('Não foi possível iniciar o checkout.');
      }
      window.location.href = url;
    } catch (error) {
      const message = getApiErrorMessage(
        error,
        'Não foi possível iniciar o pagamento. Verifique sua conexão e tente novamente.'
      );
      setErrorMessage(message);
      toast.error(message);
      setSubmitting(false);
    }
  };

  const nextDiscountPercent =
    DISCOUNT_BY_COUNT[Math.min(summary.discountProductCount + 1, 5)] || 0;
  const canIncreaseDiscount =
    !checkoutLockedForExistingSubscription &&
    summary.billableCount > 0 &&
    remainingAvailableCount > 0 &&
    nextDiscountPercent > summary.discountPercent;
  const discountProductLabel = summary.discountProductCount === 1 ? 'produto' : 'produtos';
  const futureIncludedSummaryLabel = summary.futureIncludedProducts.length === 1
    ? `${summary.futureIncludedProducts[0].name} incluso no AtendaBot`
    : 'Produtos inclusos no AtendaBot';
  const monthlyDifferencePrefix = summary.amountAddedCents < 0 ? '- ' : '+ ';
  const isSingleProductCheckout = allowedProductKeySet !== null
    && checkoutProductCatalog.length === 1;

  return (
    <div
      style={styles.overlay}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !submitting) onClose?.();
      }}
    >
      <section
        style={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header style={styles.header}>
          <div>
            <p style={{ margin: 0, color: '#0891b2', fontSize: 12, fontWeight: 800, textTransform: 'uppercase' }}>
              Checkout {checkoutBrandName}
            </p>
            <h2 style={{ margin: '6px 0 0', color: '#0f172a', fontSize: 22, lineHeight: 1.2 }}>
              {title}
            </h2>
            <p style={{ margin: '8px 0 0', color: '#64748b', fontSize: 14 }}>
              {checkoutDescription}
            </p>
          </div>
          <button type="button" style={styles.iconButton} onClick={onClose} disabled={submitting} aria-label="Fechar">
            <X size={18} />
          </button>
        </header>

        <div style={styles.body}>
          {checkoutLockedForExistingSubscription && (
            <div
              style={{
                border: '1px solid rgba(148, 163, 184, 0.36)',
                background: '#f8fafc',
                color: '#475569',
                borderRadius: 14,
                padding: 12,
                fontSize: 13,
                fontWeight: 700,
                marginBottom: 14,
              }}
            >
              Somente o proprietário pode alterar produtos de uma assinatura ativa.
            </div>
          )}

          {scopedAssignedPromotion && (
            <div
              style={{
                border: '1px solid rgba(5, 150, 105, 0.28)',
                background: '#ecfdf5',
                color: '#065f46',
                borderRadius: 14,
                padding: 12,
                fontSize: 13,
                fontWeight: 700,
                marginBottom: 14,
              }}
            >
              {isSingleProductCheckout
                ? `Oferta ${scopedAssignedPromotion.code} aplicada.`
                : `Oferta ${scopedAssignedPromotion.code} aplicada. Os produtos promocionais já estão selecionados.`}
            </div>
          )}

          {catalogLookupFailed && canCheckoutWithFixedPromotionWithoutCatalog && (
            <div
              style={{
                border: '1px solid rgba(245, 158, 11, 0.32)',
                background: '#fffbeb',
                color: '#92400e',
                borderRadius: 14,
                padding: 12,
                fontSize: 13,
                fontWeight: 700,
                marginBottom: 14,
              }}
            >
              Não foi possível atualizar os preços de catálogo, mas os valores fixos da sua oferta
              estão disponíveis e serão validados no pagamento.
            </div>
          )}

          {catalogBlocksCheckout && (
            <div
              style={{
                border: '1px solid rgba(220, 38, 38, 0.28)',
                background: '#fef2f2',
                color: '#991b1b',
                borderRadius: 14,
                padding: 12,
                fontSize: 13,
                fontWeight: 700,
                marginBottom: 14,
              }}
            >
              Não foi possível carregar os preços atuais. Tente novamente.
            </div>
          )}

          {canIncreaseDiscount && (
            <div
              style={{
                border: '1px solid rgba(245, 158, 11, 0.32)',
                background: '#fffbeb',
                color: '#92400e',
                borderRadius: 14,
                padding: 12,
                fontSize: 13,
                fontWeight: 700,
                marginBottom: 14,
              }}
            >
              Adicione mais um produto para subir o desconto progressivo para {nextDiscountPercent}%.
            </div>
          )}

          <div style={{ display: 'grid', gap: 10 }}>
            {checkoutProductCatalog.map((product) => {
              const isActive = activeKeys.has(product.key);
              const isSelected = selectedSet.has(product.key);
              const isIncludedByAtendabot = product.key === 'one_menu' && (
                selectedSet.has('atendabot') || activeKeys.has('atendabot')
              );
              const isRequiredByPromotion = assignedPromotionProductKeys.has(product.key);
              const isChecked = isActive || isSelected || isIncludedByAtendabot;
              const disabled =
                isActive
                || isIncludedByAtendabot
                || isRequiredByPromotion
                || submitting
                || checkoutLockedForExistingSubscription;
              const currentProductPrice = summary.currentProductPriceByKey[product.key];
              const futureProductPrice = summary.futureProductPriceByKey[product.key];
              const showDiscountedSelectedPrice =
                isSelected &&
                futureProductPrice &&
                futureProductPrice.finalPriceCents < product.priceCents;

              return (
                <label
                  key={product.key}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr auto',
                    alignItems: 'center',
                    gap: 12,
                    padding: 14,
                    borderRadius: 14,
                    border: isSelected || isIncludedByAtendabot
                      ? '1px solid rgba(8, 145, 178, 0.42)'
                      : '1px solid rgba(15, 23, 42, 0.10)',
                    background: isSelected || isIncludedByAtendabot ? '#ecfeff' : '#ffffff',
                    opacity: isActive ? 0.62 : 1,
                    cursor: disabled ? 'default' : 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={disabled}
                    onChange={() => toggleProduct(product.key)}
                    style={{ width: 18, height: 18, accentColor: '#0891b2' }}
                  />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', color: '#0f172a', fontWeight: 800, fontSize: 14 }}>
                      {product.name}
                    </span>
                    <span style={{ display: 'block', color: '#64748b', fontSize: 12, marginTop: 3 }}>
                      {product.description}
                    </span>
                  </span>
                  <span
                    style={{
                      textAlign: 'right',
                      color: '#0f172a',
                      fontWeight: 800,
                      fontSize: 13,
                      display: 'grid',
                      gap: 2,
                      justifyItems: 'end',
                    }}
                  >
                    {isActive ? (
                      <>
                        <span>Ativo</span>
                        {currentProductPrice && (
                          <span style={{ color: '#64748b', fontWeight: 700, whiteSpace: 'nowrap' }}>
                            {formatCurrency(currentProductPrice.finalPriceCents)}/mês
                          </span>
                        )}
                      </>
                    ) : isIncludedByAtendabot ? (
                      <>
                        <span style={{ color: '#dc2626', whiteSpace: 'nowrap' }}>
                          - {formatCurrency(product.priceCents)}/mês
                        </span>
                        <span style={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                          Incluso no AtendaBot
                        </span>
                      </>
                    ) : showDiscountedSelectedPrice ? (
                      <>
                        <span style={{ color: '#94a3b8', textDecoration: 'line-through' }}>
                          {formatCurrency(product.priceCents)}
                        </span>
                        <span style={{ color: '#047857', whiteSpace: 'nowrap' }}>
                          {formatCurrency(futureProductPrice.finalPriceCents)}/mês
                        </span>
                      </>
                    ) : (
                      formatCurrency(product.priceCents)
                    )}
                  </span>
                </label>
              );
            })}
          </div>

          <div
            style={{
              marginTop: 16,
              borderRadius: 16,
              border: '1px solid rgba(15, 23, 42, 0.10)',
              background: '#f8fafc',
              padding: 16,
            }}
          >
            <h3 style={{ margin: '0 0 6px', color: '#0f172a', fontSize: 16, lineHeight: 1.25 }}>
              Resumo da mensalidade
            </h3>
            <p style={{ margin: '0 0 12px', color: '#475569', fontSize: 13, lineHeight: 1.45 }}>
              {checkoutSummaryDescription}
            </p>
            {summary.activeCount > 0 && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                  <span style={{ color: '#475569', fontSize: 13 }}>Mensalidade atual</span>
                  <strong style={{ color: '#0f172a' }}>{formatCurrency(summary.currentTotalCents)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                  <span style={{ color: '#475569', fontSize: 13 }}>Produtos ativos no pacote</span>
                  <strong style={{ color: '#0f172a' }}>{summary.activeCount}</strong>
                </div>
              </>
            )}
            {!isSingleProductCheckout && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                <span style={{ color: '#475569', fontSize: 13 }}>Produtos que contam para o desconto</span>
                <strong style={{ color: '#0f172a' }}>
                  {summary.discountProductCount} {discountProductLabel}
                </strong>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
              <span style={{ color: '#475569', fontSize: 13 }}>Mensalidade sem desconto</span>
              <strong style={{ color: '#0f172a' }}>{formatCurrency(summary.futureSubtotalCents)}</strong>
            </div>
            {summary.futureIncludedCents > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                <span style={{ color: '#475569', fontSize: 13 }}>{futureIncludedSummaryLabel}</span>
                <strong style={{ color: '#dc2626' }}>
                  - {formatCurrency(summary.futureIncludedCents)}
                </strong>
              </div>
            )}
            {summary.promotionDiscountCents > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                <span style={{ color: '#475569', fontSize: 13 }}>Desconto promocional</span>
                <strong style={{ color: '#047857' }}>
                  - {formatCurrency(summary.promotionDiscountCents)}
                </strong>
              </div>
            )}
            {summary.progressiveDiscountCents > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                <span style={{ color: '#475569', fontSize: 13 }}>
                  {isSingleProductCheckout ? 'Desconto aplicado' : 'Desconto progressivo aplicado'} ({summary.discountPercent}%)
                </span>
                <strong style={{ color: '#047857' }}>
                  - {formatCurrency(summary.progressiveDiscountCents)}
                </strong>
              </div>
            )}
            {summary.activeCount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                <span style={{ color: '#475569', fontSize: 13 }}>Mudança na mensalidade</span>
                <strong style={{ color: summary.amountAddedCents < 0 ? '#047857' : '#0f172a' }}>
                  {monthlyDifferencePrefix}
                  {formatCurrency(Math.abs(summary.amountAddedCents))}
                </strong>
              </div>
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                paddingTop: 10,
                borderTop: '1px solid rgba(15, 23, 42, 0.10)',
              }}
            >
              <span style={{ color: '#0f172a', fontSize: 15, fontWeight: 800 }}>Total mensal atualizado</span>
              <strong style={{ color: '#0f172a', fontSize: 18 }}>{formatCurrency(summary.futureTotalCents)}</strong>
            </div>
          </div>

          {errorMessage && (
            <p style={{ margin: '12px 0 0', color: '#b91c1c', fontSize: 13, fontWeight: 700 }}>
              {errorMessage}
            </p>
          )}
        </div>

        <footer style={styles.footer}>
          <button type="button" style={styles.secondaryButton} onClick={onClose} disabled={submitting}>
            Fechar
          </button>
          <button
            type="button"
            style={{
              ...styles.primaryButton,
              opacity: summary.billableCount === 0 || submitting || promotionLoading || promotionLookupFailed || catalogLoading || catalogBlocksCheckout || checkoutLockedForExistingSubscription ? 0.58 : 1,
              cursor: summary.billableCount === 0 || submitting || promotionLoading || promotionLookupFailed || catalogLoading || catalogBlocksCheckout || checkoutLockedForExistingSubscription ? 'not-allowed' : 'pointer',
            }}
            onClick={handleSubmit}
            disabled={summary.billableCount === 0 || submitting || promotionLoading || promotionLookupFailed || catalogLoading || catalogBlocksCheckout || checkoutLockedForExistingSubscription}
          >
            {submitting ? <Check size={17} /> : <ShoppingCart size={17} />}
            {submitting
              ? 'Abrindo pagamento...'
              : promotionLoading
                ? 'Verificando oferta...'
                : catalogLoading
                  ? 'Carregando preços...'
                : checkoutLockedForExistingSubscription
                  ? 'Somente proprietário'
                  : 'Ir para pagamento'}
          </button>
        </footer>
      </section>
    </div>
  );
};

export default ProductCheckoutModal;
